import React, { useState, useEffect, useMemo } from "react";
import ReportCard from "../components/mobile/Report";
import ReportHeader from "../components/common/Report";
import ReportTable from "../components/desktop/Report";
import Pagination from '../components/Pagination';
import Spinner from '../components/ui/Spinner';
import ColumnSelectorModal from '../components/modals/ColumnSelectorModal';
import { toast } from 'react-toastify';
import { downloadReportsXLSX, getReports } from "../services/reports";
import { getISTDateString } from "../utils/datetime";
import { useCustomers } from '../context/customers';
import { USER_PROFILE, USER_ROLE } from '../services/auth';

const EMPTY_FILTERS = {
  trip_type: '',
  trip_status: '',
  branch: '',
  driver: '',
  assigned_vehicle: '',
  requested_vehicle: '',
  booking_date: '',
  placement_pickup_date: '',
  actual_in_pickup_date: '',
  actual_out_pickup_date: '',
  expected_pickup_date: '',
  actual_in_delivery_date: '',
  actual_out_delivery_date: '',
  expected_delivery_date: '',
};

const Reports = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay  = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const [fromDate, setFromDate]             = useState(getISTDateString(firstDay));
  const [toDate, setToDate]                 = useState(getISTDateString(lastDay));
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [selectedClient, setSelectedClient]     = useState('all');
  const [search, setSearch]                 = useState('');
  const [filters, setFilters]               = useState(EMPTY_FILTERS);
  const [reports, setReports]               = useState([]);
  const [count, setCount]                   = useState(0);
  const [currentPage, setCurrentPage]       = useState(1);
  const [pendingPage, setPendingPage]       = useState(null);
  const [loading, setLoading]               = useState(false);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const reportsPerPage = 20;

  const role      = localStorage.getItem(USER_ROLE);
  const profile   = useMemo(() => JSON.parse(localStorage.getItem(USER_PROFILE) || '{}'), []);
  const isAdmin   = role === 'admin';
  const branchId  = Number(profile?.branch?.id || profile?.branch || 0);
  const customerParams = useMemo(
    () => isAdmin
      ? { status: 'active', paginate: false }
      : { status: 'active', paginate: false, branch: branchId },
    [isAdmin, branchId]
  );
  const { customers } = useCustomers({ reload: true, params: customerParams });

  const selectedCustomerMisFields = useMemo(() => {
    if (selectedCustomer === 'all' || !customers.length) return [];
    const customer = customers.find(c => String(c.id) === String(selectedCustomer));
    return customer?.mis_fields || [];
  }, [selectedCustomer, customers]);

  // Auto-fetch when date range changes
  useEffect(() => { setPendingPage(1); }, [fromDate, toDate]);

  useEffect(() => {
    if (pendingPage == null) return;
    fetchReports(pendingPage, { validation_err: false });
  }, [pendingPage]);

  const buildActiveFilters = () => {
    const active = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) active[k] = v;
    });
    return active;
  };

  const fetchReports = async (page, { validation_err = true } = {}) => {
    if (!fromDate || !toDate) {
      if (validation_err) toast.error('From and To date should be selected.');
      return;
    }
    setLoading(true);
    const selectedCustomerId = selectedCustomer === 'all' ? null : Number(selectedCustomer);
    const selectedClientId   = selectedClient   === 'all' ? null : Number(selectedClient);
    const params = {
      from: fromDate,
      to: toDate,
      customer: selectedCustomerId,
      client: selectedClientId,
      page: page || 1,
      pageSize: reportsPerPage,
      paginate: true,
      filters: buildActiveFilters(),
    };
    const [err, data] = await getReports(params).catch(() => [true, null]);
    if (!err && data) {
      setReports(data.results);
      setCount(data.count);
      setCurrentPage(page);
    }
    setPendingPage(null);
    setLoading(false);
  };

  const handleViewReports = () => setPendingPage(1);

  const onFilterChange = (e, key) => {
    setFilters(prev => ({ ...prev, [key]: e.target.value }));
  };

  const onClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPendingPage(1);
  };

  const handleDownload = async () => {
    if (selectedCustomer !== 'all' && selectedCustomerMisFields.length > 0) {
      setColumnModalOpen(true);
    } else {
      await triggerDownload(null);
    }
  };

  const handleColumnConfirm = async (selectedKeys) => {
    setColumnModalOpen(false);
    await triggerDownload(selectedKeys);
  };

  const triggerDownload = async (columns) => {
    setDownloadLoading(true);
    const selectedCustomerId = selectedCustomer === 'all' ? null : Number(selectedCustomer);
    const selectedClientId   = selectedClient   === 'all' ? null : Number(selectedClient);
    try {
      const response = await downloadReportsXLSX({
        from: fromDate,
        to: toDate,
        customer: selectedCustomerId,
        client: selectedClientId,
        ...(columns ? { columns } : {}),
        filters: buildActiveFilters(),
      });
      const blobUrl = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'reports.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Failed to download report.');
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="p-4 w-full">
      <ReportHeader
        fromDate={fromDate}
        toDate={toDate}
        selectedCustomer={selectedCustomer}
        selectedClient={selectedClient}
        search={search}
        filters={filters}
        onFromDateSelected={e => setFromDate(e.target.value)}
        onToDateSelected={e => setToDate(e.target.value)}
        onCustomerChange={e => { setSelectedCustomer(e.target.value); setPendingPage(1); }}
        onClientChange={e => { setSelectedClient(e.target.value); setPendingPage(1); }}
        onSearch={e => setSearch(e.target.value)}
        onFilterChange={onFilterChange}
        onClearFilters={onClearFilters}
        handleViewReports={handleViewReports}
        handleDownload={handleDownload}
        downloadLoading={downloadLoading}
      />

      {loading ? <Spinner /> : (
        <>
          <ReportTable reports={reports} />
          <ReportCard reports={reports} />
        </>
      )}

      {reports.length > 0 && (
        <div className="md:static fixed bottom-0 left-0 w-full bg-white border-t shadow z-10">
          <Pagination
            currentPage={currentPage}
            totalPages={Math.max(1, Math.ceil(count / reportsPerPage))}
            onPageChange={setPendingPage}
          />
        </div>
      )}

      <ColumnSelectorModal
        isOpen={columnModalOpen}
        columns={selectedCustomerMisFields}
        onConfirm={handleColumnConfirm}
        onClose={() => setColumnModalOpen(false)}
      />
    </div>
  );
};

export default Reports;
